/**
 * Alta y edición de la financiación de un inmueble: préstamos, seguros y gastos recurrentes.
 *
 * UN SOLO PANEL PARA LAS TRES COSAS, y no es por ahorrar ficheros: los tres formularios tienen
 * la misma forma (unos importes, un intervalo de fechas y poco más), el mismo ciclo de guardado
 * y los mismos errores. Tres componentes casi idénticos habrían divergido a la tercera
 * corrección.
 *
 * Los importes viajan como STRING, nunca como `number`. En la base son `numeric(14,2)` y
 * convertirlos a coma flotante en la frontera JSON reintroduce el error que el esquema evita a
 * propósito. El único número de verdad es el tipo de interés, que la API sí espera como
 * `number` porque es un porcentaje, no dinero.
 */
import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { InsurancePolicyDto, LoanDto, RecurringExpenseDto, UnitDto } from '@naar/shared';
import { ApiError, api } from '../../api/client';
import { Button } from '../../components/Button';
import { Drawer } from '../../components/overlay/Drawer';
import {
  Checkbox,
  Field,
  FormRow,
  Input,
  MoneyInput,
  Select,
} from '../../components/form/Field';
import { toast } from '../../components/feedback/Toaster';

export type FinancingKind = 'loan' | 'insurance' | 'expense';

export type FinancingRecord = LoanDto | InsurancePolicyDto | RecurringExpenseDto;

const RATE_TYPE_OPTIONS = [
  { value: 'fija', label: 'Fija' },
  { value: 'variable', label: 'Variable' },
  { value: 'mixta', label: 'Mixta' },
];

const FREQUENCY_OPTIONS = [
  { value: 'mensual', label: 'Mensual' },
  { value: 'trimestral', label: 'Trimestral' },
  { value: 'anual', label: 'Anual' },
];

const COPY: Record<FinancingKind, { create: string; edit: string; subtitle: string }> = {
  loan: {
    create: 'Nuevo préstamo',
    edit: 'Editar préstamo',
    subtitle: 'La cuota mensual entra directa en el desglose de gastos del inmueble.',
  },
  insurance: {
    create: 'Nueva póliza',
    edit: 'Editar póliza',
    subtitle: 'La prima se reparte entre doce meses: no se duplica como gasto recurrente.',
  },
  expense: {
    create: 'Nuevo gasto recurrente',
    edit: 'Editar gasto recurrente',
    subtitle: 'El importe se guarda tal y como se gira; el desglose lo normaliza a mensual.',
  },
};

interface FormState {
  lender: string;
  principalAmount: string;
  outstandingBalance: string;
  interestRate: string;
  rateType: string;
  monthlyPayment: string;
  insurer: string;
  policyNumber: string;
  coverageType: string;
  premium: string;
  concept: string;
  amount: string;
  frequency: string;
  dayOfMonth: string;
  unitId: string;
  active: boolean;
  startDate: string;
  endDate: string;
}

const EMPTY: FormState = {
  lender: '',
  principalAmount: '',
  outstandingBalance: '',
  interestRate: '',
  rateType: 'fija',
  monthlyPayment: '',
  insurer: '',
  policyNumber: '',
  coverageType: '',
  premium: '',
  concept: '',
  amount: '',
  frequency: 'mensual',
  dayOfMonth: '',
  unitId: '',
  active: true,
  startDate: '',
  endDate: '',
};

function hydrate(kind: FinancingKind, record: FinancingRecord | undefined): FormState {
  if (!record) return EMPTY;

  if (kind === 'loan') {
    const loan = record as LoanDto;
    return {
      ...EMPTY,
      lender: loan.lender,
      principalAmount: loan.principalAmount,
      outstandingBalance: loan.outstandingBalance,
      interestRate: String(loan.interestRate),
      rateType: loan.rateType,
      monthlyPayment: loan.monthlyPayment,
      startDate: loan.startDate,
      endDate: loan.endDate ?? '',
    };
  }

  if (kind === 'insurance') {
    const policy = record as InsurancePolicyDto;
    return {
      ...EMPTY,
      insurer: policy.insurer,
      policyNumber: policy.policyNumber ?? '',
      coverageType: policy.coverageType ?? '',
      premium: policy.premium,
      startDate: policy.startDate,
      endDate: policy.endDate,
    };
  }

  const expense = record as RecurringExpenseDto;
  return {
    ...EMPTY,
    concept: expense.concept,
    amount: expense.amount,
    frequency: expense.frequency,
    dayOfMonth: expense.dayOfMonth === null ? '' : String(expense.dayOfMonth),
    unitId: expense.unit?.id ?? '',
    active: expense.active,
    startDate: expense.startDate,
    endDate: expense.endDate ?? '',
  };
}

function bodyOf(kind: FinancingKind, form: FormState): Record<string, unknown> {
  const endDate = form.endDate || null;

  if (kind === 'loan') {
    return {
      lender: form.lender.trim(),
      principalAmount: form.principalAmount || '0',
      outstandingBalance: form.outstandingBalance || '0',
      interestRate: Number(form.interestRate || 0),
      rateType: form.rateType,
      startDate: form.startDate,
      endDate,
      monthlyPayment: form.monthlyPayment || '0',
    };
  }

  if (kind === 'insurance') {
    return {
      insurer: form.insurer.trim(),
      policyNumber: form.policyNumber.trim() || null,
      coverageType: form.coverageType.trim() || null,
      premium: form.premium || '0',
      startDate: form.startDate,
      endDate: form.endDate,
    };
  }

  return {
    concept: form.concept.trim(),
    amount: form.amount || '0',
    frequency: form.frequency,
    dayOfMonth: form.dayOfMonth ? Number(form.dayOfMonth) : null,
    startDate: form.startDate,
    endDate,
    unitId: form.unitId || null,
    active: form.active,
  };
}

function isValid(kind: FinancingKind, form: FormState): boolean {
  if (!form.startDate) return false;
  if (kind === 'loan') {
    return (
      form.lender.trim().length > 0 &&
      Number(form.principalAmount) > 0 &&
      Number(form.monthlyPayment) > 0
    );
  }
  if (kind === 'insurance') {
    return form.insurer.trim().length > 0 && Number(form.premium) > 0 && Boolean(form.endDate);
  }
  return form.concept.trim().length > 0 && Number(form.amount) > 0;
}

interface FinancingFormDrawerProps {
  open: boolean;
  kind: FinancingKind;
  propertyId: string;
  /** Si viene, el panel edita en vez de crear. */
  record?: FinancingRecord;
  /** Para asignar un gasto a una habitación concreta. */
  units: UnitDto[];
  onClose: () => void;
  onSaved: () => void;
}

export function FinancingFormDrawer({
  open,
  kind,
  propertyId,
  record,
  units,
  onClose,
  onSaved,
}: FinancingFormDrawerProps) {
  const editing = Boolean(record);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [dirty, setDirty] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;
    setForm(hydrate(kind, record));
    setDirty(false);
    setFieldErrors({});
  }, [open, kind, record]);

  function patch(next: Partial<FormState>) {
    setForm((current) => ({ ...current, ...next }));
    setDirty(true);
  }

  const save = useMutation({
    mutationFn: () => {
      const body = bodyOf(kind, form);
      const id = record?.id ?? '';
      if (kind === 'loan') return editing ? api.updateLoan(id, body) : api.createLoan(propertyId, body);
      if (kind === 'insurance') {
        return editing ? api.updateInsurance(id, body) : api.createInsurance(propertyId, body);
      }
      return editing ? api.updateExpense(id, body) : api.createExpense(propertyId, body);
    },
    onSuccess: () => {
      onSaved();
      toast.success(editing ? 'Cambios guardados.' : 'Añadido.');
      setDirty(false);
      onClose();
    },
    onError: (error: unknown) => {
      if (!(error instanceof ApiError)) {
        toast.error('No se ha podido guardar.');
        return;
      }
      const errors: Record<string, string> = {};
      for (const detail of error.fieldErrors) errors[detail.path] = detail.message;
      setFieldErrors(errors);
      if (Object.keys(errors).length === 0) toast.error(error.message);
    },
  });

  const copy = COPY[kind];
  const valid = isValid(kind, form);

  return (
    <Drawer
      open={open}
      title={editing ? copy.edit : copy.create}
      subtitle={copy.subtitle}
      onClose={onClose}
      dirty={dirty}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={save.isPending}>
            Cancelar
          </Button>
          <Button onClick={() => save.mutate()} disabled={!valid || save.isPending}>
            {save.isPending ? 'Guardando…' : editing ? 'Guardar cambios' : 'Añadir'}
          </Button>
        </>
      }
    >
      {kind === 'loan' && (
        <>
          <Field label="Entidad" required error={fieldErrors.lender}>
            <Input
              value={form.lender}
              onChange={(event) => patch({ lender: event.target.value })}
              placeholder="Banco Santander"
              maxLength={160}
              invalid={Boolean(fieldErrors.lender)}
            />
          </Field>

          <FormRow>
            <Field label="Capital concedido" required error={fieldErrors.principalAmount}>
              <MoneyInput
                value={form.principalAmount}
                onValueChange={(value) => patch({ principalAmount: value })}
                suffix="€"
                invalid={Boolean(fieldErrors.principalAmount)}
              />
            </Field>
            <Field
              label="Saldo pendiente"
              required
              error={fieldErrors.outstandingBalance}
              help="No puede superar al capital concedido."
            >
              <MoneyInput
                value={form.outstandingBalance}
                onValueChange={(value) => patch({ outstandingBalance: value })}
                suffix="€"
                invalid={Boolean(fieldErrors.outstandingBalance)}
              />
            </Field>
          </FormRow>

          <FormRow>
            <Field label="Tipo de interés" required error={fieldErrors.interestRate}>
              <Input
                value={form.interestRate}
                onChange={(event) => patch({ interestRate: event.target.value })}
                numeric
                inputMode="decimal"
                suffix="%"
                placeholder="2,9"
                invalid={Boolean(fieldErrors.interestRate)}
              />
            </Field>
            <Field label="Modalidad" required>
              <Select
                options={RATE_TYPE_OPTIONS}
                value={form.rateType}
                onChange={(event) => patch({ rateType: event.target.value })}
              />
            </Field>
            <Field label="Cuota mensual" required error={fieldErrors.monthlyPayment}>
              <MoneyInput
                value={form.monthlyPayment}
                onValueChange={(value) => patch({ monthlyPayment: value })}
                suffix="€"
                invalid={Boolean(fieldErrors.monthlyPayment)}
              />
            </Field>
          </FormRow>
        </>
      )}

      {kind === 'insurance' && (
        <>
          <Field label="Aseguradora" required error={fieldErrors.insurer}>
            <Input
              value={form.insurer}
              onChange={(event) => patch({ insurer: event.target.value })}
              placeholder="Mapfre"
              maxLength={160}
              invalid={Boolean(fieldErrors.insurer)}
            />
          </Field>

          <FormRow>
            <Field label="Nº de póliza" optional error={fieldErrors.policyNumber}>
              <Input
                value={form.policyNumber}
                onChange={(event) => patch({ policyNumber: event.target.value })}
                maxLength={80}
              />
            </Field>
            <Field label="Cobertura" optional>
              <Input
                value={form.coverageType}
                onChange={(event) => patch({ coverageType: event.target.value })}
                placeholder="Hogar · continente y contenido"
                maxLength={120}
              />
            </Field>
          </FormRow>

          <Field
            label="Prima del periodo"
            required
            error={fieldErrors.premium}
            help="La del periodo completo de la póliza, normalmente un año."
          >
            <MoneyInput
              value={form.premium}
              onValueChange={(value) => patch({ premium: value })}
              suffix="€"
              invalid={Boolean(fieldErrors.premium)}
            />
          </Field>
        </>
      )}

      {kind === 'expense' && (
        <>
          <Field label="Concepto" required error={fieldErrors.concept}>
            <Input
              value={form.concept}
              onChange={(event) => patch({ concept: event.target.value })}
              placeholder="IBI, comunidad, internet…"
              maxLength={160}
              invalid={Boolean(fieldErrors.concept)}
            />
          </Field>

          <FormRow>
            <Field label="Importe" required error={fieldErrors.amount}>
              <MoneyInput
                value={form.amount}
                onValueChange={(value) => patch({ amount: value })}
                suffix="€"
                invalid={Boolean(fieldErrors.amount)}
              />
            </Field>
            <Field label="Frecuencia" required help="Se normaliza a mensual en el desglose.">
              <Select
                options={FREQUENCY_OPTIONS}
                value={form.frequency}
                onChange={(event) => patch({ frequency: event.target.value })}
              />
            </Field>
            <Field label="Día del mes" optional error={fieldErrors.dayOfMonth}>
              <Input
                value={form.dayOfMonth}
                onChange={(event) => patch({ dayOfMonth: event.target.value })}
                numeric
                inputMode="numeric"
                placeholder="1"
                invalid={Boolean(fieldErrors.dayOfMonth)}
              />
            </Field>
          </FormRow>

          {units.length > 0 && (
            <Field
              label="Habitación"
              optional
              help="Solo si el gasto es de una habitación concreta y no del inmueble entero."
            >
              <Select
                options={[
                  { value: '', label: 'Todo el inmueble' },
                  ...units.map((unit) => ({ value: unit.id, label: unit.code })),
                ]}
                value={form.unitId}
                onChange={(event) => patch({ unitId: event.target.value })}
              />
            </Field>
          )}

          <Checkbox
            checked={form.active}
            onChange={(event) => patch({ active: event.target.checked })}
            label="Activo"
          />
        </>
      )}

      <FormRow>
        <Field label="Fecha de inicio" required error={fieldErrors.startDate}>
          <Input
            type="date"
            value={form.startDate}
            onChange={(event) => patch({ startDate: event.target.value })}
            invalid={Boolean(fieldErrors.startDate)}
          />
        </Field>
        <Field
          label="Fecha de fin"
          required={kind === 'insurance'}
          optional={kind !== 'insurance'}
          error={fieldErrors.endDate}
        >
          <Input
            type="date"
            value={form.endDate}
            onChange={(event) => patch({ endDate: event.target.value })}
            invalid={Boolean(fieldErrors.endDate)}
          />
        </Field>
      </FormRow>
    </Drawer>
  );
}
